/**
 * InMocean Data Service
 * Handles REAL Google Cloud Storage uploads and calls REAL backend Cloud Functions.
 */

import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions'; // Import Firebase Functions
// ✅ UPDATED: Added missing Firestore functions
import { getFirestore, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore'; 
import { INMOCEAN_FILE_SCHEMAS } from '../config/fileSchemas';


class InMoceanDataServiceClass {
    constructor() {
        this.storage = null;
        this.functions = null; // Add functions instance
        this.db = null; // Add firestore instance
        this.bucketPrefix = 'inmocean-raw-data';

        console.log('🔧 InMoceanDataService initialized');
    }

    /**
     * Initialize Firebase service connections
     */
    async initialize() {
        try {
            if (!this.storage) {
                this.storage = getStorage();
                console.log('✅ InMocean storage initialized');
            }
            if (!this.functions) {
                this.functions = getFunctions();
                console.log('✅ Firebase Functions initialized');
            }
             if (!this.db) {
                this.db = getFirestore();
                console.log('✅ Firestore initialized for real-time updates');
            }
        } catch (error) {
            console.error('❌ Failed to initialize Firebase services:', error);
            throw error;
        }
    }

    /**
     * Uploads a file to Cloud Storage, which triggers the backend processing function.
     */
    async uploadAndProcessFile(file, fileType, preValidatedData = null) {
        try {
            console.log(`🚀 Starting upload for ${fileType}:`, file.name);
            await this.initialize();

            // The ONLY job of the frontend is to upload the file.
            // The backend `processFileToBigQuery` function handles the rest.
            const storageUrl = await this.uploadToStorage(file, fileType);
            
            const result = {
                fileName: file.name,
                rowCount: preValidatedData ? preValidatedData.length : 0, // Use pre-validated count for UI
                uploadTime: new Date().toISOString(),
                fileSize: file.size,
                storageUrl: storageUrl,
                status: 'processing' // The backend is now processing the file
            };
            
            console.log(`✅ File uploaded for processing for ${fileType}:`, result);
            return result;

        } catch (error) {
            console.error(`❌ Upload failed for ${fileType}:`, error);
            throw error;
        }
    }

    /**
     * Upload file to Google Cloud Storage
     */
    async uploadToStorage(file, fileType) {
        try {
            if (!this.storage) {
                await this.initialize();
            }

            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `${fileType}/${timestamp}_${file.name}`;
            const storageRef = ref(this.storage, `${this.bucketPrefix}/${filename}`);

            console.log(`📤 Uploading to storage: ${filename}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            console.log(`✅ File uploaded to storage: ${downloadURL}`);
            return downloadURL;

        } catch (error) {
            console.error('❌ Storage upload failed:', error);
            throw new Error(`Storage upload failed: ${error.message}`);
        }
    }
    
    /**
     * Listens for real-time updates from the `processing_jobs` collection in Firestore.
     */
    listenToUploadHistory(callback) {
        this.initialize();
        const historyCollectionRef = collection(this.db, 'processing_jobs');
        
        const q = query(historyCollectionRef, orderBy('startTime', 'desc'), limit(50));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const history = {};
            // Initialize all file types so the UI doesn't flicker
            Object.keys(INMOCEAN_FILE_SCHEMAS).forEach(ft => {
                history[ft] = { status: 'pending', rowCount: 0, lastUpload: null };
            });

            // First pass: find the most recent completed job for each file type to get accurate row counts
            const completedJobs = {};
            querySnapshot.forEach((doc) => {
                const job = doc.data();
                if (job.status === 'complete' && job.details?.rowsInserted > 0) {
                    if (!completedJobs[job.fileType] || job.startTime.toDate() > completedJobs[job.fileType].startTime.toDate()) {
                        completedJobs[job.fileType] = job;
                    }
                }
            });

            // Second pass: update history with most recent job status, preserving row counts from completed jobs
            querySnapshot.forEach((doc) => {
                const job = doc.data();
                // If we haven't recorded a more recent job for this file type, update it.
                if (!history[job.fileType] || (job.startTime.toDate() > (history[job.fileType].lastUpload ? new Date(history[job.fileType].lastUpload) : 0))) {
                    history[job.fileType] = {
                        lastUpload: job.startTime.toDate().toISOString(),
                        // Use rowsInserted from this job if complete, otherwise use from most recent completed job
                        rowCount: (job.status === 'complete' && job.details?.rowsInserted)
                            ? job.details.rowsInserted
                            : (completedJobs[job.fileType]?.details?.rowsInserted || 0),
                        status: job.status,
                        fileName: job.fileName,
                        error: job.status === 'error' ? job.details?.error : null
                    };
                }
            });
            console.log('🔄 Upload history updated from Firestore listener.');
            callback(history);
        }, (error) => {
            console.error("❌ Error listening to upload history:", error);
        });

        return unsubscribe; // Return the unsubscribe function to be called on cleanup
    }

    /**
     * Query data by calling the real `getBigQueryData` Cloud Function.
     */
    async queryTableData(fileType, limit = 100) {
        try {
            await this.initialize();

            console.log(`🔍 Calling "getBigQueryData" function for file type: ${fileType}`);

            const getBigQueryData = httpsCallable(this.functions, 'getBigQueryData');
            const result = await getBigQueryData({ fileType: fileType, limit: limit });

            const data = result.data.data; // The Cloud Function returns { data: rows }, so we need result.data.data

            console.log(`✅ Real-time query completed: ${data?.length || 0} rows returned`);
            return data || [];

        } catch (error) {
            console.error('❌ BigQuery query via Cloud Function failed:', error);
            // Re-throw the error so the UI can display it
            throw new Error(`Failed to query ${fileType} data: ${error.message}`);
        }
    }

    /**
     * Check for duplicate records in a table (full record match)
     */
    async checkForDuplicates(fileType) {
        try {
            await this.initialize();

            console.log(`🔍 Checking for duplicates in ${fileType}...`);

            const checkDuplicates = httpsCallable(this.functions, 'checkDuplicates');
            const result = await checkDuplicates({ fileType: fileType });

            const data = result.data;

            console.log(`✅ Duplicate check completed for ${fileType}:`, data);
            return {
                totalRecords: data.totalRecords || 0,
                uniqueRecords: data.uniqueRecords || 0,
                duplicateCount: data.duplicateCount || 0,
                duplicateGroups: data.duplicateGroups || []
            };

        } catch (error) {
            console.error('❌ Duplicate check failed:', error);
            throw new Error(`Failed to check duplicates for ${fileType}: ${error.message}`);
        }
    }

    /**
     * Remove duplicate records from a table (keeping first occurrence)
     */
    async removeDuplicates(fileType) {
        try {
            await this.initialize();

            console.log(`🗑️ Removing duplicates from ${fileType}...`);

            const removeDuplicates = httpsCallable(this.functions, 'removeDuplicates');
            const result = await removeDuplicates({ fileType: fileType });

            const data = result.data;

            console.log(`✅ Duplicates removed from ${fileType}:`, data);
            return {
                removedCount: data.removedCount || 0,
                remainingRecords: data.remainingRecords || 0
            };

        } catch (error) {
            console.error('❌ Remove duplicates failed:', error);
            throw new Error(`Failed to remove duplicates for ${fileType}: ${error.message}`);
        }
    }
}

// Export singleton instance
export const InMoceanDataService = new InMoceanDataServiceClass();
